import { Capacitor } from '@capacitor/core';

import {
  TOP_NAV_HEIGHT,
  TOP_NAV_SPACING_AFFORDANCE as TOP_NAV_SPACING_AFFORDANCE_BASE,
} from 'clipsal-cortex-ui/src/components/MobileTopNav';

import packageJSON from '../../package.json';
import { ENV_TYPE_SHORTHAND, SCHNEIDER_CHARGE_ENV_TYPE_STORAGE_KEY } from '../env-type';
import { REGION_SHORTHAND } from '../region-type';

const viteEnv = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;

export const BOTTOM_NAV_HEIGHT = 'calc(64px + env(safe-area-inset-bottom))';

export const ANDROID_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.se.chargenam';
export const APPLE_APP_STORE_URL = 'https://apps.apple.com/us/app/schneider-charge/id6758716842';

export const COMPONENT_MIN_HEIGHT = 'calc(100vh - env(safe-area-inset-bottom) - env(safe-area-inset-top))';

export const APP_VERSION = packageJSON.version;

// Need to include env override in the evaluation because capacitor builds are always in production mode
export const IS_PRODUCTION = viteEnv?.MODE === 'production';
export const IS_PRODUCTION_DATA = localStorage.getItem(SCHNEIDER_CHARGE_ENV_TYPE_STORAGE_KEY) === 'PROD';
export const IS_NOT_DEVELOPMENT = viteEnv?.MODE !== 'development';
export const IS_RUNNING_CYPRESS_TESTS = (window as Window & typeof globalThis).Cypress;
export const IS_RUNNING_NATIVE_TESTS = viteEnv?.VITE_TEST_TYPE === 'APPIUM';
export const IS_RUNNING_TESTS = IS_RUNNING_CYPRESS_TESTS || IS_RUNNING_NATIVE_TESTS;
export const IS_RUNNING_HEADLESS =
  /HeadlessChrome/.test(window.navigator.userAgent) || /headless/i.test(navigator.appVersion);
export const IS_PRODUCTION_BUILD = IS_NOT_DEVELOPMENT && !IS_RUNNING_TESTS && !IS_RUNNING_HEADLESS;

export const ENDPOINT = viteEnv?.[`VITE_${REGION_SHORTHAND}_${ENV_TYPE_SHORTHAND}_ENDPOINT`] as string;

export const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const WEEKDAYS_TO_API_VALUES: Record<string, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

/**
 * Returns the WEEKDAYS array ordered according to the given week start day.
 * @param weekStartsOn 0 = Sunday, 1 = Monday, etc.
 */
export function getOrderedWeekdays(weekStartsOn: number): string[] {
  const startIndex = WEEKDAYS.indexOf(
    Object.entries(WEEKDAYS_TO_API_VALUES).find(([, v]) => v === weekStartsOn)?.[0] ?? 'Sunday'
  );
  return [...WEEKDAYS.slice(startIndex), ...WEEKDAYS.slice(0, startIndex)];
}

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const IS_WEB = Capacitor.getPlatform() === 'web';
export const IS_IOS = Capacitor.getPlatform() === 'ios';
export const IS_ANDROID = Capacitor.getPlatform() === 'android';
export const IS_NATIVE = Capacitor.isNativePlatform();

// Session storage keys
export const ATT_STATUS_STORAGE_KEY = 'pendingAttStatus';

// ─── App-wide colour palette ──────────────────────────────────────────────────
// Use these instead of inline hex values. Never redeclare these locally in a
// component file — import from here.
// ─── Top banners ──────────────────────────────────────────────────────────────
// Top banners are always 32px in height
export const TOP_BANNER_HEIGHT = '32px';
export const TOP_BANNER_SPACING_AFFORDANCE = 'calc(32px + env(safe-area-inset-top))';
// Show banner for non-prod data
// If banner is shown, add its height to the top nav spacing affordance
export const ADD_BANNER_SPACING = !IS_PRODUCTION_DATA;
export const TOP_NAV_SPACING_AFFORDANCE = ADD_BANNER_SPACING
  ? `calc(${TOP_NAV_HEIGHT + 10}px + ${TOP_BANNER_HEIGHT})`
  : TOP_NAV_SPACING_AFFORDANCE_BASE;

export const MAIN_SCREEN_WITH_BOTTOM_NAV_HEIGHT = `calc(100vh - ${TOP_NAV_SPACING_AFFORDANCE} - ${BOTTOM_NAV_HEIGHT})`;
